import { Grid, Typography } from "@mui/material";
import {
  LoadingButton,
  UploadButton,
} from "../../../../components/button/Button";
import { useDownloadBlobAsFile } from "../../../../hooks/useDownloadBlobAsFile";
import { ENV_LISTS } from "../../../../core/EnvTranspiler";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { useImportProduct } from "../../api/product/resolver";
import { Control, UseFormSetValue } from "react-hook-form";
import Input from "../../../../components/input/Input-v2";
import { useGetCategory } from "../../api/product/category/resolver";
import useGenerateOptions from "../../../../components/input/hook/useGenerateOptions2";
import { useGetBrands } from "../../api/product/brand/resolver";
import { useNavigate } from "react-router";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

export interface MainFilterGroup {
  max_price: string;
  min_price: string;
  product_category_id: string;
  product_brand_id: string;
  stock: string;
  has_tax: string;
}

export const KEY_URL_PARAM = {
  max_price: "max_price",
  min_price: "min_price",
  product_category_id: "product_category_id",
  product_brand_id: "product_brand_id",
  stock: "stock",
  has_tax: "has_tax",
};

const TableHeaderExtra = () => {
  const renderRight = () => {
    const { mutate: mutateUpload, isLoading: isLoadingUpload } =
      useImportProduct();
    const { download, isLoading: isLoadingDownload } = useDownloadBlobAsFile();
    const urlDownload = `${ENV_LISTS.API_URL}/product/export`;

    return (
      <Grid container spacing={2} maxWidth={250}>
        <Grid item xs={12} mt={2}>
          <LoadingButton
            style={{ width: "100%" }}
            onClick={() => download(urlDownload, "Produk_Export")}
            isLoading={isLoadingDownload}
            text="Export"
            icon={<CloudDownloadIcon />}
          />
          <Typography variant="caption" mt={1}>
            Export data produk menjadi <strong>.csv</strong>
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <UploadButton
            style={{ width: "100%" }}
            text="Upload"
            onChange={(e) => {
              if (e) {
                mutateUpload({ file: e[0] });
              }
            }}
            isLoading={isLoadingUpload}
          />
          <Typography variant="caption" mt={1}>
            Import data produk dengan file <strong>.csv</strong>
          </Typography>
        </Grid>
      </Grid>
    );
  };

  const renderFilter = ({
    control,
    setValue,
  }: {
    control: Control<MainFilterGroup, any>;
    setValue: UseFormSetValue<MainFilterGroup>;
  }) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [keywordKategori, setKeywordKategori] = useState("");
    const [keywordBran, setKeywordBran] = useState("");
    const { data: dataRawCategories, isLoading: fetchCategories } =
      useGetCategory({ page: 1, keyword: keywordKategori });
    const dataCategories = dataRawCategories?.data.data.data;
    const { data: dataCategoriesOptions } = useGenerateOptions({
      dataSource: dataCategories,
      keyForText: "category_name",
      keyForValue: "id",
    });
    const { data: dataRawBrands } = useGetBrands({
      keyword: keywordBran,
      page: 1,
    });
    const dataBrands = dataRawBrands?.data.data.data;
    const { data: dataBrandOptions } = useGenerateOptions({
      dataSource: dataBrands,
      keyForText: "brand_name",
      keyForValue: "id",
    });

    const handleOnChange = (key: string, value: string) => {
      const searchParams = new URLSearchParams(location.search);
      searchParams.set(key, value); // Append or update param
      navigate(`${location.pathname}?${searchParams.toString()}`, {
        replace: true,
      });
    };

    useEffect(() => {
      const allParams = Object.fromEntries(searchParams.entries());
      Object.keys(KEY_URL_PARAM).forEach((item) => {
        if (allParams[item]) {
          setValue(item as any, allParams[item]);
        }
      });
    }, []);

    return (
      <Grid container spacing={2} maxWidth={400}>
        <Grid item xs={12}>
          <Input.Controller
            control={control}
            name="product_brand_id"
            render={(field) => (
              <Input.Autocomplete
                options={dataBrandOptions}
                label="Brand Produk"
                value={field.value}
                onChange={(e) => setKeywordBran(e)}
                onClick={(e) => {
                  handleOnChange(KEY_URL_PARAM.product_brand_id, e.value);
                  field.onChange(e.value);
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Input.Controller
            control={control}
            name="product_category_id"
            render={(field) => (
              <Input.Autocomplete
                options={dataCategoriesOptions}
                label="Kategori Produk"
                value={field.value}
                onChange={(e) => setKeywordKategori(e)}
                onClick={(e) => {
                  handleOnChange(KEY_URL_PARAM.product_category_id, e.value);
                  field.onChange(e.value);
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Input.Controller
            control={control}
            name="max_price"
            render={(field) => (
              <Input.Text
                isFetching={fetchCategories}
                label="Harga Maksimal"
                value={field.value}
                type="number"
                onChange={(e) => {
                  handleOnChange(KEY_URL_PARAM.max_price, e);
                  field.onChange(e);
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Input.Controller
            control={control}
            name="min_price"
            render={(field) => (
              <Input.Text
                isFetching={fetchCategories}
                label="Harga Minimal"
                value={field.value}
                type="number"
                onChange={(e) => {
                  handleOnChange(KEY_URL_PARAM.min_price, e);
                  field.onChange(e);
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Input.Controller
            control={control}
            name="stock"
            render={(field) => (
              <Input.Text
                isFetching={fetchCategories}
                label="Stock Produk"
                value={field.value}
                type="number"
                onChange={(e) => {
                  handleOnChange(KEY_URL_PARAM.stock, e);
                  field.onChange(e);
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Input.Controller
            control={control}
            name="has_tax"
            render={(field) => (
              <Input.Select
                label="Produk Dengan Pajak"
                options={[
                  { text: "Ya", value: 1 },
                  { text: "Tidak", value: -1 },
                ]}
                value={field.value}
                onChange={(e) => {
                  handleOnChange(KEY_URL_PARAM.has_tax, e.value);
                  field.onChange(e.value);
                }}
              />
            )}
          />
        </Grid>
        {/* <Grid item xs={12}>
          <IconOnButton
            text="Hapus Semua Filter"
            icon={<div></div>}
            handleClick={() => {
              navigate(location.pathname, { replace: true });
            }}
          />
        </Grid> */}
      </Grid>
    );
  };

  return {
    renderRight,
    renderFilter,
  };
};

export default TableHeaderExtra;
